import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { fetchAPI } from '../../utils/api';
import { FaUserCircle, FaSave, FaKey, FaSchool, FaUserFriends, FaMapMarkedAlt } from 'react-icons/fa';

export default function Profile() {
    const { currentUser, changePassword } = useAuth();
    const [profileData, setProfileData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        birthday: '',
        age: '',
        address: '',
        school_name: '',
        grade: '',
        stream: '',
        guardian_name: '',
        guardian_contact: '',
        address_line1: '',
        address_line2: '',
        district: '',
        city: ''
    });
    const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
    const [message, setMessage] = useState({ type: '', text: '' });

    // Fetch profile data from database
    useEffect(() => {
        const loadProfile = async () => {
            try {
                const data = await fetchAPI('/common/get_profile.php');
                setProfileData(data);
                setFormData({
                    name: data.name || '',
                    email: data.email || '',
                    phone: data.phone || '',
                    birthday: data.birthday || '',
                    age: data.age || '',
                    address: data.address || '',
                    school_name: data.school_name || '',
                    grade: data.grade || '',
                    stream: data.stream || '',
                    guardian_name: data.guardian_name || '',
                    guardian_contact: data.guardian_contact || '',
                    address_line1: data.address_line1 || '',
                    address_line2: data.address_line2 || '',
                    district: data.district || '',
                    city: data.city || ''
                });
            } catch (err) {
                console.error('Failed to load profile:', err);
                setMessage({ type: 'error', text: 'Failed to load profile data' });
            } finally {
                setLoading(false);
            }
        };
        loadProfile();
    }, []);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handlePasswordChange = (e) => {
        setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await fetchAPI('/common/update_profile.php', {
                method: 'POST',
                body: JSON.stringify(formData)
            });
            setMessage({ type: 'success', text: 'Profile updated successfully!' });
            // Reload profile data
            const data = await fetchAPI('/common/get_profile.php');
            setProfileData(data);
        } catch (err) {
            setMessage({ type: 'error', text: 'Failed to update profile: ' + err.message });
        }
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    };

    if (loading) {
        return (
            <div className="max-w-4xl mx-auto p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <p className="mt-4 text-gray-500 font-medium">Loading profile...</p>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 lemon-milk">My Profile</h2>

            {message.text && (
                <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {message.text}
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Profile Card */}
                <div className="md:col-span-1">
                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 text-center">
                        <div className="mb-4 flex justify-center">
                            <FaUserCircle className="text-gray-300 text-9xl" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">{profileData?.name || 'User'}</h3>
                        <p className="text-sm text-gray-500 mb-4">{profileData?.email || 'No email'}</p>
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold uppercase tracking-wide">
                            {profileData?.role || 'user'}
                        </span>
                    </div>
                </div>

                {/* Edit Form */}
                <div className="md:col-span-2 space-y-6">
                    <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                        <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2 flex items-center gap-2">
                            <FaUserCircle /> Personal Information
                        </h3>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                                <input name="name" value={formData.name} onChange={handleChange} className="w-full border rounded-lg p-2.5 bg-gray-50 focus:bg-white transition-colors" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                                <input name="phone" value={formData.phone} onChange={handleChange} className="w-full border rounded-lg p-2.5 bg-gray-50 focus:bg-white transition-colors" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Birthday</label>
                                <input name="birthday" type="date" value={formData.birthday} onChange={handleChange} className="w-full border rounded-lg p-2.5 bg-gray-50 focus:bg-white transition-colors" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                                <input name="age" type="number" value={formData.age} onChange={handleChange} className="w-full border rounded-lg p-2.5 bg-gray-50 focus:bg-white transition-colors" />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                                <textarea name="address" value={formData.address} onChange={handleChange} className="w-full border rounded-lg p-2.5 bg-gray-50 focus:bg-white transition-colors" rows="2" />
                            </div>
                        </div>

                        {/* School Information Section */}
                        <div className="mt-6 pt-6 border-t border-gray-100">
                            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <FaSchool /> School Information
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">School Name</label>
                                    <input name="school_name" value={formData.school_name} onChange={handleChange} className="w-full border rounded-lg p-2.5 bg-gray-50 focus:bg-white transition-colors" placeholder="Enter your school name" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
                                    <select name="grade" value={formData.grade} onChange={handleChange} className="w-full border rounded-lg p-2.5 bg-gray-50 focus:bg-white transition-colors">
                                        <option value="">Select Grade</option>
                                        <option value="6">Grade 6</option>
                                        <option value="7">Grade 7</option>
                                        <option value="8">Grade 8</option>
                                        <option value="9">Grade 9</option>
                                        <option value="10">Grade 10</option>
                                        <option value="11">Grade 11</option>
                                        <option value="12">Grade 12</option>
                                        <option value="13">Grade 13</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Stream</label>
                                    <select name="stream" value={formData.stream} onChange={handleChange} className="w-full border rounded-lg p-2.5 bg-gray-50 focus:bg-white transition-colors">
                                        <option value="">Select Stream</option>
                                        <option value="Science">Science</option>
                                        <option value="Commerce">Commerce</option>
                                        <option value="Arts">Arts</option>
                                        <option value="Technology">Technology</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Guardian Information Section */}
                        <div className="mt-6 pt-6 border-t border-gray-100">
                            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <FaUserFriends /> Guardian Information
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Guardian Name</label>
                                    <input name="guardian_name" value={formData.guardian_name} onChange={handleChange} className="w-full border rounded-lg p-2.5 bg-gray-50 focus:bg-white transition-colors" placeholder="Parent/Guardian name" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Guardian's Contact Number</label>
                                    <input name="guardian_contact" value={formData.guardian_contact} onChange={handleChange} className="w-full border rounded-lg p-2.5 bg-gray-50 focus:bg-white transition-colors" placeholder="07XXXXXXXX" />
                                </div>
                            </div>
                        </div>

                        {/* Delivery Details Section */}
                        <div className="mt-6 pt-6 border-t border-gray-100">
                            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <FaMapMarkedAlt /> Delivery Details
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
                                    <input name="address_line1" value={formData.address_line1} onChange={handleChange} className="w-full border rounded-lg p-2.5 bg-gray-50 focus:bg-white transition-colors" placeholder="House number, Street name" />
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
                                    <input name="address_line2" value={formData.address_line2} onChange={handleChange} className="w-full border rounded-lg p-2.5 bg-gray-50 focus:bg-white transition-colors" placeholder="Area, Landmark (optional)" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
                                    <select name="district" value={formData.district} onChange={handleChange} className="w-full border rounded-lg p-2.5 bg-gray-50 focus:bg-white transition-colors">
                                        <option value="">Select District</option>
                                        <option value="Ampara">Ampara</option>
                                        <option value="Anuradhapura">Anuradhapura</option>
                                        <option value="Badulla">Badulla</option>
                                        <option value="Batticaloa">Batticaloa</option>
                                        <option value="Colombo">Colombo</option>
                                        <option value="Galle">Galle</option>
                                        <option value="Gampaha">Gampaha</option>
                                        <option value="Hambantota">Hambantota</option>
                                        <option value="Jaffna">Jaffna</option>
                                        <option value="Kalutara">Kalutara</option>
                                        <option value="Kandy">Kandy</option>
                                        <option value="Kegalle">Kegalle</option>
                                        <option value="Kilinochchi">Kilinochchi</option>
                                        <option value="Kurunegala">Kurunegala</option>
                                        <option value="Mannar">Mannar</option>
                                        <option value="Matale">Matale</option>
                                        <option value="Matara">Matara</option>
                                        <option value="Monaragala">Monaragala</option>
                                        <option value="Mullaitivu">Mullaitivu</option>
                                        <option value="Nuwara Eliya">Nuwara Eliya</option>
                                        <option value="Polonnaruwa">Polonnaruwa</option>
                                        <option value="Puttalam">Puttalam</option>
                                        <option value="Ratnapura">Ratnapura</option>
                                        <option value="Trincomalee">Trincomalee</option>
                                        <option value="Vavuniya">Vavuniya</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                                    <input name="city" value={formData.city} onChange={handleChange} className="w-full border rounded-lg p-2.5 bg-gray-50 focus:bg-white transition-colors" placeholder="City/Town" />
                                </div>
                            </div>
                        </div>

                        {profileData?.role === 'student' && (
                            <div className="mt-6 pt-6 border-t border-gray-100">
                                <h4 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wider">Academic Information</h4>
                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1 uppercase">Index Number</label>
                                        <div className="font-mono bg-gray-100 p-2 rounded text-gray-700">{profileData?.indexNumber || 'N/A'}</div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1 uppercase">Grade</label>
                                        <div className="font-mono bg-gray-100 p-2 rounded text-gray-700">{profileData?.grade || 'N/A'}</div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1 uppercase">Year</label>
                                        <div className="font-mono bg-gray-100 p-2 rounded text-gray-700">{profileData?.year || 'N/A'}</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="mt-6 flex justify-end">
                            <button type="submit" className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center gap-2">
                                <FaSave /> Save Changes
                            </button>
                        </div>
                    </form>

                    {/* Change Password */}
                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 transition-all">
                        <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2 flex items-center gap-2">
                            <FaKey /> Security
                        </h3>
                        <p className="text-sm text-gray-500 mb-4">Change your account password below.</p>
                        <form
                            onSubmit={async (e) => {
                                e.preventDefault();
                                if (passwordData.new !== passwordData.confirm) {
                                    return setMessage({ type: 'error', text: 'New passwords do not match' });
                                }
                                try {
                                    const res = await changePassword(passwordData.current, passwordData.new);
                                    setMessage({ type: 'success', text: res.message });
                                    setPasswordData({ current: '', new: '', confirm: '' });
                                } catch (err) {
                                    setMessage({ type: 'error', text: err.message });
                                }
                            }}
                            className="space-y-4"
                        >
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Current Password</label>
                                    <input
                                        type="password"
                                        name="current"
                                        value={passwordData.current}
                                        onChange={handlePasswordChange}
                                        className="w-full border rounded-lg p-2.5 bg-gray-50 focus:bg-white transition-colors"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase mb-1">New Password</label>
                                    <input
                                        type="password"
                                        name="new"
                                        value={passwordData.new}
                                        onChange={handlePasswordChange}
                                        className="w-full border rounded-lg p-2.5 bg-gray-50 focus:bg-white transition-colors"
                                        required
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Confirm New Password</label>
                                    <input
                                        type="password"
                                        name="confirm"
                                        value={passwordData.confirm}
                                        onChange={handlePasswordChange}
                                        className="w-full border rounded-lg p-2.5 bg-gray-50 focus:bg-white transition-colors"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end">
                                <button type="submit" className="bg-gray-900 text-white px-6 py-2 rounded-lg font-medium hover:bg-gray-800 transition-colors">
                                    Update Password
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}
